import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from "lucide-react";
import { DynamicChart } from "./DynamicChart";
import { EmptyState } from "@/components/ui/EmptyState";
import { LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { useSavedCharts } from "@/hooks/useSavedCharts";
import {
  aggregate,
  computeKpi,
  listDataSources,
  loadSourceRows,
  type SourceOption,
} from "@/lib/dashboard-data";
import { categoricalColumns, numericColumns } from "@/lib/ml";

type WidgetKind = "kpi" | "bar" | "line" | "pie";

export const CustomizableWidgets = () => {
  const { charts, isLoading, saveChart, deleteChart, moveChart } = useSavedCharts();

  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [sourceId, setSourceId] = useState("");
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [kind, setKind] = useState<WidgetKind>("kpi");
  const [title, setTitle] = useState("");
  const [xField, setXField] = useState("");
  const [yField, setYField] = useState("");
  const [how, setHow] = useState<"sum" | "avg" | "count" | "max" | "min">("sum");

  useEffect(() => {
    if (open && !sources.length) {
      listDataSources().then(setSources).catch(() => setSources([]));
    }
  }, [open, sources.length]);

  const numeric = numericColumns(rows);
  const labels = categoricalColumns(rows, 20).filter((c) => !numeric.includes(c));

  const chooseSource = async (id: string) => {
    setSourceId(id);
    setLoadingRows(true);
    try {
      const loaded = await loadSourceRows(id);
      setRows(loaded);
      setYField(numericColumns(loaded)[0] ?? "");
      setXField(categoricalColumns(loaded, 20)[0] ?? "");
    } catch (e: any) {
      setRows([]);
      toast.error(e?.message || "Could not load that data");
    } finally {
      setLoadingRows(false);
    }
  };

  const addWidget = async () => {
    if (!rows.length) return toast.error("Choose your data first.");
    if (!title.trim()) return toast.error("Give the widget a title.");
    if (kind !== "kpi" && (!xField || !yField)) return toast.error("Choose both columns.");
    if (kind === "kpi" && how !== "count" && !yField) return toast.error("Choose a number column.");

    const sourceName = sources.find((s) => s.id === sourceId)?.label ?? null;

    const data =
      kind === "kpi"
        ? [{ label: title.trim(), value: computeKpi(rows, yField, how), how, field: yField }]
        : aggregate(rows, xField, yField, how === "avg" ? "avg" : "sum", kind === "pie" ? 8 : 20);

    if (!data.length) return toast.error("No usable values in those columns.");

    await saveChart.mutateAsync({
      title: title.trim(),
      chartType: kind,
      xField: kind === "kpi" ? null : xField,
      yField,
      aggregation: how,
      data,
      origin: "widget",
      sourceType: sourceId.split(":")[0],
      sourceName,
      sourceId,
    });
    toast.success("Widget added");
    setOpen(false);
    setTitle("");
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Your dashboard</h2>
            <p className="text-sm text-muted-foreground">
              Saved charts and KPIs, calculated from your own data.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Add widget
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add a widget</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Your data</Label>
                  <Select value={sourceId} onValueChange={chooseSource}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={sources.length ? "Choose your data" : "No data available yet"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {sources.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {loadingRows && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Reading rows...
                    </p>
                  )}
                  {rows.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{rows.length} rows loaded</p>
                  )}
                </div>

                <div>
                  <Label>Widget type</Label>
                  <Select value={kind} onValueChange={(v: WidgetKind) => setKind(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kpi">Single number (KPI)</SelectItem>
                      <SelectItem value="bar">Bar chart</SelectItem>
                      <SelectItem value="line">Line chart</SelectItem>
                      <SelectItem value="pie">Pie chart</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Total revenue" />
                </div>

                {kind !== "kpi" && (
                  <div>
                    <Label>Group by</Label>
                    <Select value={xField} onValueChange={setXField}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a column" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...labels, ...numeric].map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Number column</Label>
                    <Select value={yField} onValueChange={setYField}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a column" />
                      </SelectTrigger>
                      <SelectContent>
                        {numeric.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Calculation</Label>
                    <Select value={how} onValueChange={(v: any) => setHow(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sum">Total</SelectItem>
                        <SelectItem value="avg">Average</SelectItem>
                        {kind === "kpi" && <SelectItem value="count">Row count</SelectItem>}
                        {kind === "kpi" && <SelectItem value="max">Highest</SelectItem>}
                        {kind === "kpi" && <SelectItem value="min">Lowest</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button className="w-full" onClick={addWidget} disabled={saveChart.isPending}>
                  {saveChart.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add to dashboard
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : charts.length === 0 ? (
        <Card className="p-6">
          <EmptyState
            icon={LayoutDashboard}
            title="Nothing on your dashboard yet"
            description="Create a chart above or add a widget — everything is built from data you've uploaded."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {charts.map((chart, index) => (
            <div key={chart.id} className="relative">
              <div className="absolute right-2 top-2 z-10 flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={index === 0}
                  onClick={() => moveChart.mutate({ id: chart.id, direction: -1 })}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={index === charts.length - 1}
                  onClick={() => moveChart.mutate({ id: chart.id, direction: 1 })}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deleteChart.mutate(chart.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {chart.chart_type === "kpi" ? (
                <Card className="p-6">
                  <h3 className="text-sm font-medium text-muted-foreground">{chart.title}</h3>
                  <div className="text-3xl font-bold mt-2">
                    {Number((chart.data as any[])[0]?.value ?? 0).toLocaleString()}
                  </div>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {chart.aggregation} of {chart.y_field} · {chart.source_name ?? "your data"}
                  </Badge>
                </Card>
              ) : (
                <DynamicChart
                  config={{
                    id: chart.id,
                    type: chart.chart_type as "bar" | "line" | "pie",
                    title: chart.title,
                    data: chart.data as any[],
                    xAxis: chart.x_field ?? undefined,
                    yAxis: chart.y_field ?? undefined,
                  }}
                  data={chart.data as any[]}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
